import React from 'react';
import { AutoComplete, Button, Input, Modal, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { standardizeFailContentKey, useAddressStandardize } from '../hooks/useAddressStandardize';
import { useLocationCascade } from '../hooks/useLocationCascade';

/**
 * 标准化后的地址四段（结构与 PickupSiteFormDrawer 的 SiteAddressForSave 一致）。
 *
 * <p>这里刻意**不 import** 那个类型：PickupSiteFormDrawer 会 import 本组件，反向 import 会成环。
 * 两者结构相同，TS 结构化类型可双向赋值。
 */
export interface StandardizedAddress {
  /** 含 dock —— 保存时后端按此列算 core_hash（口径见下方说明） */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  standardized?: boolean;
}

/** 分段地址的受控值。 */
export interface PickupAddressValue {
  /** 街道（**不含** dock、**不含** 小号） */
  street?: string;
  /** 小号（Ste/Unit/Apt，选填）。仅录入/展示，**不进命中口径**：不发去标准化、不拼进街道，存 addressLine2。 */
  unit?: string;
  /** Dock，选填 */
  dock?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  /** 已标准化标记：任一字段变更即置 false，保存前必须为 true */
  standardized?: boolean;
  /** 标准化成功后地址库返回的结果（addressLine1 已拼回 dock），保存取这份而非用户原始输入 */
  resolved?: StandardizedAddress;
}

interface Props {
  value?: PickupAddressValue;
  onChange?: (v: PickupAddressValue) => void;
  readOnly?: boolean;
  /** 标准化成功回调（如 AB 向导据此触发同址站点搜索） */
  onStandardized?: (resolved: StandardizedAddress) => void;
  /** 紧凑布局：AB 映射行 / 向导内使用，字段挤在两行 */
  compact?: boolean;
  /** 标准化按钮右侧的额外操作 */
  extraAction?: React.ReactNode;
  /**
   * 覆盖标准化按钮文案。AB 向导那里"标准化 + 搜同址站点"是一次点击完成的
   * （onStandardized 里接着发搜索），按钮写「搜索」比「地址标准化」更贴合运营心智，
   * 拆成两个按钮会让运营多点一次。
   */
  standardizeLabel?: string;
  /**
   * 本表单不做地址标准化（2.1 需求点1「确认无法建联」终态建站/更新站）：
   * 隐藏「地址标准化」按钮，**并抑制所有"请点击标准化后再保存"类橙色警告**，改为一句中性说明。
   *
   * <p>该地址在入池时就已经标准化失败过一次，按钮点了只会再失败一次；而走到这一步运营两次都没联系上
   * 客户，也无从问出正确地址。产品原话："标准化失败的选择确认无法建联，就不需要强制校验地址是否标准化了，
   * 这样就建不出来站点了"。
   *
   * <p><b>提示必须和按钮一起抑制</b>：只放行提交、留着警告，等于叫运营去点一个已经不存在的按钮——
   * 那种界面没人敢按确定，功能照样不可用。
   */
  standardizeSkipped?: boolean;
}

/**
 * 揽收地址分段输入（揽收站点 2.0）。街道 / Dock / 州 / 城市 / 邮编 五段 + 地址标准化。
 *
 * <p><b>为什么分段</b>：单文本框方案无论前端按逗号拆还是后端 parseRawAddress 拆，都在"猜哪段是什么"，
 * 街道自带逗号时（`123 Main St, Apt 4B, Brooklyn, NY 11205`）会把公寓号当城市、城市当州并丢弃中间段。
 * 分段输入直接消灭解析环节。
 *
 * <p><b>dock 与 core_hash 的口径</b>（关键，改错会导致命中大面积漏）：
 * <ul>
 *   <li>调地址库标准化时传**不含 dock** 的街道 —— 地址库对小号解析弱，含 dock 会干扰识别</li>
 *   <li>标准化返回后把 dock **拼回** `resolved.addressLine1`（空格分隔、置于末尾）</li>
 *   <li>保存时 `addressLine1` 含 dock、`dock` 列另存 —— 与后端
 *       `cooperationWarehouseAddressAnalyze`（parseDockInfo 剥离 → 调库 → restorePrimaryNumberAndDock 拼回）
 *       及 DDL 注释 `address_core_hash COMMENT '标准化+dock'` 完全一致</li>
 * </ul>
 *
 * <p><b>州/城市/邮编用 AutoComplete 而非 Select</b>：联动数据源是 zipcode 基础表，实测 1909 个站点里
 * 有 196 个（约 10%）的 city+zipcode 组合不在该表中。用严格 Select 会让这些真实地址**录不进来**，
 * 比解析错更严重。AutoComplete = 有建议但允许自定义输入；自定义时给出提示要求必须走标准化校验。
 */
const PickupAddressFields: React.FC<Props> = ({
  value,
  onChange,
  readOnly,
  onStandardized,
  compact,
  extraAction,
  standardizeLabel,
  standardizeSkipped = false,
}) => {
  const { t } = useTranslation();
  const wt = (k: string) => t(`pickup.addressReviewPool.siteForm.${k}`) as string;
  const { standardize, loading: stdLoading } = useAddressStandardize();

  const v = value ?? {};

  const { stateOptions, cityOptions, zipcodeOptions, isCustom, filterOption } = useLocationCascade(
    v.state,
    v.city,
  );
  const hasCustom =
    isCustom(v.state, stateOptions) || isCustom(v.city, cityOptions) || isCustom(v.zipcode, zipcodeOptions);

  /** 任一字段变更都要重置已标准化标记与 resolved —— 否则会拿旧标准化结果保存新地址 */
  const patch = (p: Partial<PickupAddressValue>) =>
    onChange?.({ ...v, ...p, standardized: false, resolved: undefined });

  const handleStandardize = async () => {
    if (!v.street?.trim() || !v.city?.trim() || !v.state?.trim() || !v.zipcode?.trim()) {
      Modal.warning({ title: wt('standardizeFailTitle'), content: wt('addressFieldsRequired') });
      return;
    }
    // 传不含 dock 的街道：地址库对小号解析弱，含 dock 会干扰大号识别（与后端 parseDockInfo 同思路）
    const res = await standardize({
      street: v.street.trim(),
      city: v.city.trim(),
      state: v.state.trim(),
      zip: v.zipcode.trim(),
    });
    if (!res.isValid || !res.normalized) {
      // 按失败原因给可执行提示：exception 可重试，no_match/idlog_empty 必须改地址
      Modal.warning({
        title: wt('standardizeFailTitle'),
        content: wt(standardizeFailContentKey(res.fallbackReason)),
      });
      onChange?.({ ...v, standardized: false, resolved: undefined });
      return;
    }
    const n = res.normalized;
    // 运营可能把小号误填进街道框 → 后端 parseDockInfo 会抓出来；Dock 框为空时自动回填，
    // 已填则以 Dock 框为准（运营显式输入优先）
    const dock = v.dock?.trim() || res.dock || undefined;
    const line1WithDock = [n.addressLine1, dock].filter(Boolean).join(' ');
    const resolved: StandardizedAddress = {
      addressLine1: line1WithDock,
      addressLine2: n.addressLine2,
      city: n.city,
      state: n.state,
      zipcode: n.zipcode,
      country: 'US',
      standardized: true,
    };
    onChange?.({
      // 必须带 ...v：小号(unit) 不参与标准化，但它是表单值的一部分。
      // 此前整体替换对象、漏掉 unit，运营填好小号再点一次「重新标准化」小号就被静默清空，
      // 保存时 addressLine2 随之丢失（buildSaveData 优先取 addrFields.unit）。
      ...v,
      // 街道框回填标准化后的写法（不含 dock），Dock 框单独展示
      street: n.addressLine1,
      dock,
      state: n.state,
      city: n.city,
      zipcode: n.zipcode,
      standardized: true,
      resolved,
    });
    onStandardized?.(resolved);
  };

  const dockW = compact ? 110 : 140;
  const unitW = compact ? 90 : 120;

  // 小号变更：不重置 standardized/resolved —— 小号不发去标准化、不影响命中(core)hash，
  // 与街道/州/城市/邮编(patch 会重置)不同，改小号无需重新校验地址。
  const patchUnit = (unit: string) => onChange?.({ ...v, unit });

  return (
    <div className="w-full">
      {/* 第一行：街道 + 小号 + Dock。街道占满剩余宽度——它最长且最容易被截断 */}
      <Space.Compact className="w-full">
        <Input
          value={v.street}
          readOnly={readOnly}
          placeholder={wt('streetPlaceholder')}
          onChange={(e) => patch({ street: e.target.value })}
          style={{ flex: 1 }}
        />
        {!compact && (
          <Input
            value={v.unit}
            readOnly={readOnly}
            placeholder={wt('unitPlaceholder')}
            maxLength={50}
            onChange={(e) => patchUnit(e.target.value)}
            style={{ width: unitW, flex: 'none' }}
          />
        )}
        <Input
          value={v.dock}
          readOnly={readOnly}
          placeholder={wt('dock')}
          maxLength={50}
          onChange={(e) => patch({ dock: e.target.value })}
          style={{ width: dockW, flex: 'none' }}
        />
      </Space.Compact>

      {/* 第二行：州 / 城市 / 邮编 + 标准化。三者等分，避免长城市名（如 Rancho Cucamonga）被挤没 */}
      <Space.Compact className="mt-1.5 w-full">
        <AutoComplete
          value={v.state}
          disabled={readOnly}
          placeholder={wt('statePlaceholder')}
          options={stateOptions}
          filterOption={filterOption}
          // 改州必须清空下级：下级选项整批换掉，留旧值会出现「NY + Los Angeles」这类不存在的组合
          onChange={(val) => patch({ state: val, city: undefined, zipcode: undefined })}
          style={{ flex: 1 }}
        />
        <AutoComplete
          value={v.city}
          disabled={readOnly}
          placeholder={wt('cityPlaceholder')}
          options={cityOptions}
          filterOption={filterOption}
          onChange={(val) => patch({ city: val, zipcode: undefined })}
          style={{ flex: 1.4 }}
        />
        <AutoComplete
          value={v.zipcode}
          disabled={readOnly}
          placeholder={wt('zipPlaceholder')}
          options={zipcodeOptions}
          filterOption={filterOption}
          onChange={(val) => patch({ zipcode: val })}
          style={{ flex: 1 }}
        />
        {!readOnly && !standardizeSkipped && (
          <Button type="primary" loading={stdLoading} onClick={handleStandardize} style={{ flex: 'none' }}>
            {standardizeLabel ?? (v.standardized ? wt('reStandardize') : wt('standardize'))}
          </Button>
        )}
        {extraAction}
      </Space.Compact>

      {v.standardized && v.resolved ? (
        <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          ✓ {wt('standardized')}
          <span className="ml-3 text-gray-600">
            {wt('detailAddress')} {v.resolved.addressLine1} · {wt('cityPlaceholder')} {v.resolved.city} ·{' '}
            {wt('statePlaceholder')} {v.resolved.state} · {wt('zipPlaceholder')} {v.resolved.zipcode}
          </span>
        </div>
      ) : (
        !readOnly &&
        (standardizeSkipped ? (
          // 中性说明而非橙色警告：运营该知道"这地址标不出来、会按原样存"，
          // 而不是被一句 ⚠ 劝退——那种界面没人敢按确定，提交放行了也依然建不出站点
          <div className="mt-1 text-[13px] text-gray-500">{wt('skipStandardizeHint')}</div>
        ) : (
          <div className="mt-1 text-[13px] text-orange-500">⚠ {wt('standardizeHint')}</div>
        ))
      )}
      {hasCustom && !v.standardized && !readOnly && !standardizeSkipped && (
        <div className="mt-1 text-[13px] text-orange-500">⚠ {wt('customValueHint')}</div>
      )}
      {!readOnly && !compact && (
        <div className="mt-1 text-[13px] text-gray-400">{wt('dockDiffHint')}</div>
      )}
    </div>
  );
};

export default PickupAddressFields;
